import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Calendar, DollarSign, Pencil, Save, X } from "lucide-react";
import { MailingHistoryTimeline } from "./MailingHistoryTimeline";

interface MailingHistory {
  id: string;
  campaign_name: string | null;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string | null;
  returned: boolean;
  response_received: boolean;
  response_date: string | null;
  response_notes: string | null;
}

export function MailingRecordDetail({ record, onClose, onUpdated }: { record: any; onClose: () => void; onUpdated?: () => void }) {
  const [history, setHistory] = useState<MailingHistory[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: record.first_name || "",
    last_name: record.last_name || "",
    suffix: record.suffix || "",
    business_name: record.business_name || "",
    street_address: record.street_address || "",
    city: record.city || "",
    state: record.state || "",
    zip_code: record.zip_code || "",
    county: record.county || "",
    origin_state: record.origin_state || "",
    property_address: record.property_address || "",
    phone_number: record.phone_number || "",
    email_address: record.email_address || "",
    document_type: record.document_type || "",
    response: record.response || "",
    mailing_status: record.mailing_status || "active",
    tm_notes: record.tm_notes || "",
    tm_dropdown: record.tm_dropdown || "",
  });

  useEffect(() => {
    fetchHistory();
  }, [record.id]);

  const fetchHistory = async () => {
    const { data } = await supabase
      .from("mailing_history")
      .select("*")
      .eq("mailing_record_id", record.id)
      .order("mailing_round", { ascending: true });
    setHistory(data || []);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_records")
      .update({
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        suffix: form.suffix || null,
        business_name: form.business_name || null,
        street_address: form.street_address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        county: form.county || null,
        origin_state: form.origin_state || null,
        property_address: form.property_address || null,
        phone_number: form.phone_number || null,
        email_address: form.email_address || null,
        document_type: form.document_type || null,
        response: form.response || null,
        mailing_status: form.mailing_status,
        tm_notes: form.tm_notes || null,
        tm_dropdown: form.tm_dropdown || null,
      })
      .eq("id", record.id);

    setSaving(false);
    if (error) {
      toast.error("Failed to save");
    } else {
      toast.success("Record updated");
      setEditing(false);
      onUpdated?.();
    }
  };

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const renderField = (label: string, field: string, type = "text", colSpan = false) => (
    <div className={colSpan ? "col-span-2" : ""} key={field}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {editing ? (
        type === "textarea" ? (
          <Textarea value={(form as any)[field]} onChange={e => update(field, e.target.value)} className="text-sm min-h-[60px]" />
        ) : (
          <Input value={(form as any)[field]} onChange={e => update(field, e.target.value)} className="text-sm h-8" />
        )
      ) : (
        <p className="text-sm font-medium">{(form as any)[field] || "—"}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with edit toggle */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-mono">ID: {record.seller_id}</p>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Edit Record
            </Button>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-3">
        {renderField("First Name", "first_name")}
        {renderField("Last Name", "last_name")}
        {renderField("Suffix", "suffix")}
        {renderField("Business Name", "business_name")}
      </div>

      {/* Mailing Address */}
      <div>
        <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Mailing Address</p>
        <div className="grid grid-cols-2 gap-3">
          {renderField("Street Address", "street_address", "text", true)}
          {renderField("City", "city")}
          {renderField("State", "state")}
          {renderField("Zip Code", "zip_code")}
          {renderField("County", "county")}
          {renderField("Origin State", "origin_state")}
        </div>
      </div>

      {/* Property & Contact */}
      <div className="grid grid-cols-2 gap-3">
        {renderField("Property Address", "property_address", "text", true)}
        {renderField("Phone Number", "phone_number")}
        {renderField("Email Address", "email_address")}
        {renderField("Document Type", "document_type")}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Status</p>
          {editing ? (
            <Select value={form.mailing_status} onValueChange={v => update("mailing_status", v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="responded">Responded</SelectItem>
                <SelectItem value="do_not_mail">Do Not Mail</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge variant={form.mailing_status === "active" ? "default" : form.mailing_status === "do_not_mail" ? "destructive" : "secondary"}>
              {form.mailing_status}
            </Badge>
          )}
        </div>
        {renderField("Response", "response")}
        {renderField("TM Dropdown", "tm_dropdown")}
        {renderField("TM Notes", "tm_notes", "textarea", true)}
      </div>

      {/* Mailing Stats */}
      <div className="flex gap-4 text-sm">
        <div><span className="text-muted-foreground">Times Mailed:</span> <strong>{record.times_mailed}</strong></div>
        <div><span className="text-muted-foreground">Last Mailed:</span> <strong>{record.last_mailed_date || "Never"}</strong></div>
        {record.consideration && (
          <div className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-muted-foreground" /> <strong>${record.consideration.toLocaleString()}</strong></div>
        )}
      </div>

      {/* Mailing History Timeline */}
      <MailingHistoryTimeline history={history} onResponseSaved={fetchHistory} recordId={record.id} />
    </div>
  );
}
